import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { ConfigService } from 'src/app/services/config.service';

@Injectable({
  providedIn: 'root'
})
export class PdfService {

  private documentReadEvents: BehaviorSubject<string> = new BehaviorSubject<string>("");
  public documentReadEvents$: Observable<string> = this.documentReadEvents.asObservable();

  pdfs: Array<PdfDocument> = new Array<PdfDocument>();

  constructor(
    private configService: ConfigService
  ) { }
  
  readyPromise: Promise<any> = new Promise<void>((resolve) => {
    if (window.AdobeDC) {
      resolve();
    } else {
      document.addEventListener('adobe_dc_view_sdk.ready', () => {
        resolve();
      });
    }
  });
  adobeDCView: any;

  ready() {
    return this.readyPromise;
  }

  previewFile(pdfUrl: string, fileName: string, divId: string, viewerConfig: any) {
    const config: any = {
      clientId: this.configService.config.adobeClientId,
    };
    if (divId) {
      config.divId = divId;
    }
    this.adobeDCView = new window.AdobeDC.View(config);

    const previewFilePromise = this.adobeDCView.previewFile({
      content: {
        location: {
          url: pdfUrl,
        },
      },
      metaData: {
        fileName: fileName,
      }
    }, viewerConfig);

    previewFilePromise.then((adobeViewer: any) => {
      this.addOrUpdatePdf({ fileName: fileName, pagesRead: new Set([1]), totalPages: 0 } as PdfDocument);
      adobeViewer.getAPIs().then((apis: any) => {
        apis.getPDFMetadata()
        .then((result: any) => {
          this.updatePdfTotalPages(fileName, result.numPages);
          this.monitorPageReads();
        })
      });
    });

    return previewFilePromise;
  }

  registerEventsHandler() {
    this.adobeDCView.registerCallback(
      window.AdobeDC.View.Enum.CallbackType.EVENT_LISTENER,
      (event: any) => {
        //console.log(event);
      },
      /* options to control the callback execution */
      {
        /* Enable PDF analytics events on user interaction. */
        enablePDFAnalytics: true,
      }
    );
  }

  monitorPageReads() {
    this.adobeDCView.registerCallback(
      window.AdobeDC.View.Enum.CallbackType.EVENT_LISTENER,
      (event: any) => {
        let page = event.data.pageNumber;
        let fileName = event.data.fileName;
        this.updatePdfPagesRead(fileName, page);
        if (this.getPdfPagesRead(fileName) === this.getPdfTotalPages(fileName)) {
          this.documentReadEvents.next(fileName);
        }
      },
      /* options to control the callback execution */
      {
        /* Enable PDF analytics events on user interaction. */
        enablePDFAnalytics: true,
      }
    );
  }

  private updatePdfPagesRead(pdfName: string, page: number) {
    let pdfIndex = this.pdfs.findIndex(x => x.fileName === pdfName);
    this.pdfs[pdfIndex].pagesRead?.add(page);
  }

  private updatePdfTotalPages(pdfName: string, totalPages: number) {
    let pdfIndex = this.pdfs.findIndex(x => x.fileName === pdfName);
    this.pdfs[pdfIndex].totalPages = totalPages;
  }

  private getPdfPagesRead(pdfName: string) {
    let pdfIndex = this.pdfs.findIndex(x => x.fileName === pdfName);
    return this.pdfs[pdfIndex].pagesRead?.size;
  }

  private getPdfTotalPages(pdfName: string) {
    let pdfIndex = this.pdfs.findIndex(x => x.fileName === pdfName);
    return this.pdfs[pdfIndex].totalPages;
  }

  private addOrUpdatePdf(pdfDocument: PdfDocument) {
    let pdfIndex = this.pdfs.findIndex(x => x.fileName === pdfDocument.fileName);
    if (pdfIndex) {
      this.pdfs[pdfIndex] = pdfDocument;
    } else {
      this.pdfs.push(pdfDocument);
    }
  }
}

class PdfDocument {
  fileName: string | undefined;
  totalPages?: number;
  pagesRead: Set<number> | undefined;
}
